import React, { useEffect, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Box, Button, Divider, Tooltip, useTheme } from '@material-ui/core'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { BuildingDrawer } from '../building/BuildingDrawer'
import {
  BuildingFragment,
  EnergyBusFragment,
  LocalGridFragment,
  LocalGridType,
} from '../../generated/graphql'
import { useBuildings } from '../../building/use-buildings'
import { useLocalGrids } from '../../local-grid/use-local-grids'
import { BuildingList } from '../building/BuildingList'
import { LocalGridList } from '../local-grid/LocalGridList'
import { LocalGridDrawer } from '../local-grid/LocalGridDrawer'
import { activeVersionVar, projectVar } from '../../apollo/reactive-vars'
import { BuildingBasicInfoDialog } from '../building/basic-info-dialog/BuildingBasicInfoDialog'
import {
  FormValues,
  mapFormValuesToBuildingInput,
} from '../building/basic-info-dialog/form-values'
import { Loader } from '../common/Loader'
import { Section } from '../common/Section'
import { LoadingDialog } from '../common/LoadingDialog'
import { useAuthorization } from '../../authorization/use-authorization'
import { useEnergyBus } from '../../energy-bus/use-energy-bus'
import { EnergyBusDrawer } from '../energy-bus/EnergyBusDrawer'
import { EnergyBusList } from '../energy-bus/EnergyBusList'

export function ProjectPage(): React.ReactElement {
  const { createBuilding, isCreateLoading: isBuildingCreateLoading } =
    useBuildings()
  const { createLocalGrid, isCreateLoading: isLocalGridCreateLoading } =
    useLocalGrids()
  const { createEnergyBus, isCreateLoading: isEnergyBusCreateLoading } =
    useEnergyBus()
  const [openedBuilding, setOpenedBuilding] = useState<
    BuildingFragment | undefined
  >()
  const [openedLocalGrid, setOpenedLocalGrid] = useState<
    LocalGridFragment | undefined
  >()
  const [openedEnergyBus, setOpenedEnergyBus] = useState<
    EnergyBusFragment | undefined
  >()
  const { t } = useTranslation()
  const [isCreatingBuilding, setIsCreatingBuilding] = useState(false)
  const theme = useTheme()

  const project = useReactiveVar(projectVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const canEditProject = useAuthorization().canEditProject(project)

  useEffect(() => {
    if (!activeVersion) {
      return
    }

    if (openedBuilding) {
      setOpenedBuilding(
        activeVersion.buildings.find(
          (building) => building.id === openedBuilding.id,
        ),
      )
    }

    if (openedLocalGrid) {
      setOpenedLocalGrid(
        activeVersion.localGrids.find(
          (localGrid) => localGrid.id === openedLocalGrid.id,
        ),
      )
    }

    if (openedEnergyBus) {
      setOpenedEnergyBus(activeVersion.energyBus ?? undefined)
    }
  }, [activeVersion, openedBuilding, openedLocalGrid, openedEnergyBus])

  if (!project || !activeVersion) {
    return <Loader />
  }

  const onAddBuildingSubmit = async (values: FormValues) => {
    setIsCreatingBuilding(false)

    const input = mapFormValuesToBuildingInput(values, false)
    await createBuilding(activeVersion, input)
  }

  const onAddLocalGridClick = async (type: LocalGridType) => {
    await createLocalGrid(activeVersion, {
      type,
      // name: t(`project.localGridNamePlaceholder.${type}`),
    })
  }

  const onAddEnergyBusClick = async () => {
    await createEnergyBus(activeVersion, { wasteHourSeriesId: null })
  }

  const localGridTypes = Object.values(LocalGridType).filter(
    (type) =>
      !activeVersion.localGrids.some((localGrid) => localGrid.type === type),
  )

  const isAddingLocalGridDisabled = (type: LocalGridType) => {
    if (!canEditProject) {
      return true
    }

    if (type === LocalGridType.Water) {
      return Boolean(activeVersion.energyBus)
    }

    return false
  }

  const isAddingEnergyBusDisabled = () => {
    if (!canEditProject) {
      return true
    }

    return activeVersion.localGrids.some(
      (localGrid) => localGrid.type === LocalGridType.Water,
    )
  }

  return (
    <Box>
      <Section
        title={t('project.localGrids')}
        addon={
          <Box display="flex" gridGap={theme.spacing(1)}>
            {localGridTypes.map((type) => (
              <Tooltip
                key={`${type}-tooltip`}
                title={t(`project.addLocalGridTooltip.${type}`) ?? ''}
                arrow
              >
                <span>
                  <Button
                    id={`add-local-grid-${type}`}
                    key={type}
                    variant="contained"
                    color="primary"
                    startIcon={<Plus />}
                    onClick={() => onAddLocalGridClick(type)}
                    disabled={isAddingLocalGridDisabled(type)}
                  >
                    {t(`project.addLocalGrid.${type}`)}
                  </Button>
                </span>
              </Tooltip>
            ))}
            {activeVersion.energyBus ? null : (
              <Tooltip
                key="energy-bus-tooltip"
                title={t('project.addEnergyBusTooltip') ?? ''}
                arrow
              >
                <span>
                  <Button
                    id="add-energy-bus"
                    key="energy-bus"
                    variant="contained"
                    color="primary"
                    startIcon={<Plus />}
                    onClick={() => onAddEnergyBusClick()}
                    disabled={isAddingEnergyBusDisabled()}
                  >
                    {t('project.addEnergyBus')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        }
      >
        <LocalGridList
          localGrids={activeVersion.localGrids}
          onLocalGridClick={(localGrid) => setOpenedLocalGrid(localGrid)}
        />

        <EnergyBusList
          energyBuses={activeVersion.energyBus ? [activeVersion.energyBus] : []}
          onEnergyBusClick={(energyBus) => setOpenedEnergyBus(energyBus)}
        />
      </Section>

      <Divider />

      <Section
        title={t('project.buildings')}
        addon={
          <Button
            id="add-building"
            variant="contained"
            color="primary"
            startIcon={<Plus />}
            onClick={() => setIsCreatingBuilding(true)}
            disabled={!canEditProject}
          >
            {t('project.addBuilding')}
          </Button>
        }
      >
        <BuildingList
          buildings={activeVersion.buildings}
          onBuildingClick={(building) => setOpenedBuilding(building)}
        />
      </Section>

      <BuildingBasicInfoDialog
        defaultValues={{ name: '', area: '', demandHourSeriesIds: [] }}
        isOpen={isCreatingBuilding}
        onClose={() => setIsCreatingBuilding(false)}
        onSubmit={onAddBuildingSubmit}
      />

      <LoadingDialog
        isLoading={
          isBuildingCreateLoading ||
          isLocalGridCreateLoading ||
          isEnergyBusCreateLoading
        }
      />

      <BuildingDrawer
        building={openedBuilding}
        onClose={() => setOpenedBuilding(undefined)}
      />

      <LocalGridDrawer
        localGrid={openedLocalGrid}
        onClose={() => setOpenedLocalGrid(undefined)}
      />

      <EnergyBusDrawer
        energyBus={openedEnergyBus}
        onClose={() => setOpenedEnergyBus(undefined)}
      />
    </Box>
  )
}
